import React from 'react';
import { Plus } from 'lucide-react';
import { computePlaysBoost, type PlaysBoostSource } from '../../../types/selfService';
import { formatMoney, formatMoneySmart } from './format';
import { tintFromAccent } from '../steps/_shared';

interface ImpressionsCalculatorProps {
  budget: number | null;
  cpm: number;
  accentColor?: string;
  compact?: boolean;
  // CTV-640: itemized play-boost contributors (e.g. promo + prepay) in
  // INTEGER percentage points, as produced by selectPlaysBoostSources.
  // Additive on the base with a single combined floor — the same math the
  // server books into pacing_impressions. When empty, the calculator shows
  // the plain baseline.
  boostSources?: PlaysBoostSource[];
  // CTV-575: when the buyer picks the monthly plan, the review page passes the
  // sum of the per-installment charges here so "Total cost" agrees with the
  // "Total over N months" schedule below it. When null/undefined the
  // calculator shows the plain budget total. (CTV-635: there is no
  // advertiser-facing service fee, the buyer pays the budget exactly.)
  totalOverride?: number | null;
}

const ImpressionsCalculator: React.FC<ImpressionsCalculatorProps> = ({
  budget,
  cpm,
  accentColor = '#3b82f6',
  compact = false,
  boostSources = [],
  totalOverride = null,
}) => {
  if (!budget || budget <= 0) return null;

  const boost = computePlaysBoost(budget, cpm, boostSources);
  const { base: baseImpressions, lines: lineItems, total: totalImpressions } = boost;
  // CTV-518: tax is 0 everywhere today. The Tax row below renders only when
  // > 0, so enabling sales tax later is a data change, not a UI build.
  const tax: number = 0;
  // CTV-635: the buyer pays the budget (+ tax) exactly, no service-fee
  // surcharge. CTV-575's monthly override keeps the card equal to the
  // installment schedule's rounding.
  const useOverride = totalOverride != null && totalOverride > 0;
  const total = useOverride ? totalOverride : budget + tax;

  if (compact) {
    return (
      <div className="flex items-center gap-4 text-sm">
        <span className="text-slate-600">
          ~<span className="text-slate-900 font-medium">{totalImpressions.toLocaleString()}</span> streaming ad plays
        </span>
        <span className="text-slate-400">|</span>
        <span className="text-slate-600">
          Total: <span className="text-slate-900 font-medium">${formatMoneySmart(total)}</span>
        </span>
      </div>
    );
  }

  const hasBonus = boost.bonus > 0;

  return (
    <div
      className="rounded-2xl border border-slate-200/80 overflow-hidden bg-white"
      style={{ boxShadow: 'var(--card-shadow)' }}
    >
      {/* Hero */}
      <div className="px-5 pt-5 pb-4 flex items-baseline gap-3">
        <div className="flex-1">
          <div className="text-[10px] font-medium uppercase tracking-[0.12em] text-slate-400">
            {hasBonus ? 'Total streaming ad plays' : 'Streaming ad plays'}
          </div>
          <div
            className="mt-1 text-3xl font-semibold tracking-tight"
            style={{ color: accentColor }}
          >
            {totalImpressions.toLocaleString()}
          </div>
        </div>
        <div className="text-right">
          <div
            className="text-[10px] font-medium uppercase tracking-[0.12em] text-slate-400"
            title="cost per 1,000 streaming ad plays"
          >
            CPM
          </div>
          <div className="mt-1 text-[13px] font-medium text-slate-600 leading-tight">
            ${formatMoneySmart(boost.effectiveCpm)}
            {hasBonus && (
              <div className="text-[11px] text-slate-400 line-through">
                ${formatMoneySmart(cpm)}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Stacked breakdown — base plays + each itemized bonus. Bonus rows
          get a tinted background and bigger emphasis so the buyer can
          immediately see what each code/plan is contributing. */}
      {hasBonus && (
        <div className="px-5 py-3 border-t border-slate-200/70 space-y-2 bg-slate-50/40">
          <div className="flex justify-between items-baseline text-[13px]">
            <span className="text-slate-500">Base plays</span>
            <span className="text-slate-700 font-medium tabular-nums">
              {baseImpressions.toLocaleString()}
            </span>
          </div>
          {lineItems.map((item, i) => (
            <div
              key={`${item.label}-${i}`}
              className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg border"
              style={{
                backgroundColor: tintFromAccent(accentColor, 0.08),
                borderColor: tintFromAccent(accentColor, 0.25),
              }}
            >
              <span className="inline-flex items-center gap-2 min-w-0">
                <span
                  className="inline-flex items-center justify-center w-5 h-5 rounded-full shrink-0"
                  style={{ backgroundColor: accentColor }}
                >
                  <Plus className="w-3 h-3 text-white" strokeWidth={3} />
                </span>
                <span className="text-[13px] font-semibold text-slate-900 truncate">
                  {item.label}
                </span>
                <span
                  className="shrink-0 px-1.5 py-0.5 rounded text-[10px] font-bold tabular-nums tracking-tight"
                  style={{ backgroundColor: accentColor, color: 'white' }}
                >
                  +{item.percent}%
                </span>
              </span>
              <span className="text-[15px] font-bold tabular-nums" style={{ color: accentColor }}>
                +{item.delta.toLocaleString()}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Cost breakdown. CTV-635: no advertiser-facing service fee. */}
      <div className="px-5 py-3 border-t border-slate-200/70 space-y-1.5">
        <Row label="Budget" value={`$${formatMoney(budget)}`} />
        {tax > 0 && <Row label="Tax" value={`$${formatMoney(tax)}`} />}
      </div>

      {/* Total cost */}
      <div
        className="px-5 py-3 border-t border-slate-200/70 flex items-center justify-between"
        style={{ backgroundColor: tintFromAccent(accentColor, 0.04) }}
      >
        <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-600">
          Total cost
        </span>
        <span className="text-[15px] font-semibold tracking-tight text-slate-900">
          ${formatMoney(total)}
        </span>
      </div>
    </div>
  );
};

const Row: React.FC<{ label: string; value: string; hint?: string }> = ({ label, value, hint }) => (
  <div className="flex justify-between items-baseline text-[13px]">
    <span className="text-slate-500">
      {label}
      {hint && <span className="ml-1.5 text-[10px] text-slate-400">{hint}</span>}
    </span>
    <span className="text-slate-700 font-medium tabular-nums">{value}</span>
  </div>
);

export default ImpressionsCalculator;
